import { useState } from "react";
import { formatKstDay, normalizeSerial, todayKst } from "@rma-orbit/shared";
import type {
  CaseDetail as Case,
  CaseUpdateRequest,
  EvidenceUploadMeta,
} from "@rma-orbit/shared";
import { api } from "../api/client.js";
import { navigate } from "../App.js";
import {
  Busy,
  CASE_STATUS_OPTIONS,
  Cell,
  ErrorNotice,
  Notice,
  ResultBadge,
  StatusBadge,
  useAsync,
} from "../lib/ui.js";

/**
 * 케이스 상세 — 한 화면에서 검수·승인·출고·송장까지 완결한다 (harness/UI-SPEC.md §1).
 *
 * 상태 전이는 막지 않는다. 비정상 이동일 때만 **확인을 묻는다**
 * (harness/RMA-PROCEDURE.md §3, SOUL S1).
 */

const ORDER = [
  "RECEIVED",
  "INSPECTING",
  "AT_VENDOR",
  "VENDOR_BACK",
  "READY_TO_RETURN",
  "RETURNED",
];

function isUnusualMove(from: string, to: string): boolean {
  const a = ORDER.indexOf(from);
  const b = ORDER.indexOf(to);
  if (a < 0 || b < 0) return false;
  return b < a || b - a > 1;
}

export function CaseDetail({ id, can }: { id: number; can: (p: string) => boolean }) {
  const detail = useAsync(() => api.caseDetail(id), [id]);
  const companies = useAsync(() => api.companies(), []);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (detail.loading) return <Busy />;
  if (detail.error) return <ErrorNotice error={detail.error} />;
  if (!detail.data) return <Notice kind="error">케이스를 찾을 수 없습니다.</Notice>;

  const row = detail.data;

  async function patch(update: CaseUpdateRequest) {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.updateCase(id, update);
      setMessage("저장했습니다.");
      detail.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 4 }}>
        <button className="ghost" onClick={() => navigate("#/cases")}>
          ← 목록
        </button>
      </div>
      <h1>{row.case_no}</h1>
      <p className="page-desc">
        <span className="mono">{row.serial_norm}</span> ·{" "}
        <a
          href={`#/devices/${encodeURIComponent(row.serial_norm)}`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`#/devices/${encodeURIComponent(row.serial_norm)}`);
          }}
        >
          이 장비의 전 이력 보기
        </a>
      </p>

      {message && <Notice kind="ok">{message}</Notice>}
      <ErrorNotice error={error} />

      <div className="card">
        <h2>기본</h2>
        <div className="row" style={{ gap: 24 }}>
          <div>
            <div className="muted">입고일</div>
            <div className="num">{row.received_at}</div>
          </div>
          <div>
            <div className="muted">상태</div>
            <StatusBadge status={row.status} />
          </div>
          <div>
            <div className="muted">결과</div>
            <ResultBadge result={row.result} />
          </div>
          <div>
            <div className="muted">담당자</div>
            <div>
              <Cell value={row.sender_name} />
            </div>
          </div>
          <div>
            <div className="muted">연락처</div>
            <div>
              <Cell value={row.sender_contact} />
            </div>
          </div>
          {row.reentry_of_case_id && (
            <div>
              <div className="muted">재입고</div>
              <a
                href={`#/cases/${row.reentry_of_case_id}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`#/cases/${row.reentry_of_case_id}`);
                }}
              >
                이전 케이스 {row.reentry_of_case_no ?? `#${row.reentry_of_case_id}`}
              </a>
            </div>
          )}
        </div>
        {!can("case.pii") && (
          <p className="muted" style={{ marginBottom: 0, marginTop: 10 }}>
            담당자·연락처는 <code>case.pii</code> 권한이 있어야 보입니다. 지금은 값이 있는지
            없는지도 보이지 않습니다.
          </p>
        )}
      </div>

      <SwapCard
        row={row}
        canEdit={can("swap.confirm")}
        onDone={(msg) => {
          setMessage(msg);
          detail.reload();
        }}
        onError={setError}
      />

      {can("case.write") && (
        <InspectCard row={row} busy={busy} onSave={patch} />
      )}

      <EvidenceCard caseId={id} canUpload={can("evidence.write")} onError={setError} />

      {can("shipment.write") && (
        <ShipCard
          caseId={id}
          receivedAt={row.received_at}
          canApprove={can("approval.decide")}
          companies={(companies.data ?? []).map((c) => ({ id: c.id, name: c.name }))}
          onDone={() => {
            setMessage("출고를 등록했습니다.");
            detail.reload();
          }}
          onError={setError}
        />
      )}
    </>
  );
}

function InspectCard({
  row,
  busy,
  onSave,
}: {
  row: { status: string; result: string | null; reported_symptom: string | null; our_finding: string | null };
  busy: boolean;
  onSave: (update: CaseUpdateRequest) => Promise<void>;
}) {
  const [symptom, setSymptom] = useState(row.reported_symptom ?? "");
  const [finding, setFinding] = useState(row.our_finding ?? "");
  const [status, setStatus] = useState(row.status);
  const [result, setResult] = useState(row.result ?? "");
  const symptoms = useAsync(() => api.frequentSymptoms(), []);

  async function save() {
    // 비정상 이동은 막지 않는다. 확인만 묻는다 (RMA-PROCEDURE §3)
    if (status !== row.status && isUnusualMove(row.status, status)) {
      const ok = window.confirm(
        `상태를 ${row.status} → ${status} 로 건너뜁니다. 실제로 그렇게 처리하셨나요?\n\n(막지는 않습니다. 그대로 기록됩니다.)`,
      );
      if (!ok) return;
    }
    await onSave({
      reported_symptom: symptom || null,
      our_finding: finding || null,
      status: status as CaseUpdateRequest["status"],
      result: (result === "" ? null : result) as CaseUpdateRequest["result"],
    });
  }

  return (
    <div className="card">
      <h2>
        검수 기록<span className="sub">모르는 것은 비워 둡니다 — 추정해 채우지 않습니다</span>
      </h2>

      <div className="row" style={{ marginBottom: 12 }}>
        <label className="field">
          상태
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {CASE_STATUS_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          결과
          <select value={result} onChange={(e) => setResult(e.target.value)}>
            <option value="">미정</option>
            <option value="OK">자체 처리 (OK)</option>
            <option value="TO_VENDOR">SECUI 이관 (TO_VENDOR)</option>
          </select>
        </label>
      </div>

      {/*
        자주 쓴 문장은 **지어낸 목록이 아니라 이미 적힌 것을 센 것**이다.
        실제 데이터에 "셋팅완료 2.0.12" 가 10번 있다 — 같은 문장을 열 번 치는 것은
        사람이 할 일이 아니다. 채워 넣기만 하고 저장은 사람이 누른다(SOUL S5).
      */}
      {(symptoms.data?.items.length ?? 0) > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div className="muted" style={{ fontSize: 13, marginBottom: 6 }}>
            자주 쓰신 증상 — 누르면 아래 칸에 채웁니다 (고쳐서 저장하세요)
          </div>
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            {symptoms.data!.items.map((s) => (
              <button
                key={s.text}
                className="ghost"
                title={s.text}
                onClick={() => setSymptom(s.text)}
              >
                {s.text.length > 28 ? `${s.text.slice(0, 28)}…` : s.text}
                <span className="muted"> ×{s.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="row" style={{ alignItems: "stretch" }}>
        <label className="field grow">
          신고 증상
          <textarea value={symptom} onChange={(e) => setSymptom(e.target.value)} />
        </label>
        <label className="field grow">
          우리 판단·조치
          <textarea value={finding} onChange={(e) => setFinding(e.target.value)} />
        </label>
      </div>

      <div className="row" style={{ marginTop: 12 }}>
        <button className="primary" disabled={busy} onClick={save}>
          {busy ? "저장 중…" : "저장"}
        </button>
      </div>
    </div>
  );
}

/**
 * 출고 등록. **승인이 없으면 서버가 409 로 막는다** (APPROVAL-POLICY §2).
 * 화면에서도 승인 번호를 반드시 받는다 — 우회 경로를 만들지 않는다.
 */
/**
 * 출고·송장 (harness/API-SPEC.md §5-1).
 *
 * 승인 번호를 **사람에게 타이핑시키지 않는다.** 예전엔 "목록에서 일괄 승인 후 받은 번호"
 * 를 적으라고 했는데, 그 번호를 보여주는 화면이 어디에도 없었다 — 화면이 안 보여주는
 * 값을 적으라고 하면 그 기능은 없는 것과 같다.
 *
 * 그래서 이 카드가 그 케이스의 승인을 직접 읽고, **방향에 맞는 승인된 건만** 고르게 한다.
 * 없으면 여기서 바로 승인할 수 있다(권한이 있을 때). 승인 잠금은 그대로다 —
 * 승인 없이 출고가 등록되는 경로는 여전히 없다(AP-01).
 */
function ShipCard({
  caseId,
  companies,
  canApprove,
  receivedAt,
  onDone,
  onError,
}: {
  caseId: number;
  receivedAt: string;
  companies: Array<{ id: number; name: string }>;
  canApprove: boolean;
  onDone: () => void;
  onError: (err: unknown) => void;
}) {
  const [direction, setDirection] = useState<"OUT_VENDOR" | "OUT_OWNER">("OUT_VENDOR");
  const [dest, setDest] = useState("");
  const [shippedAt, setShippedAt] = useState(todayKst());
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  const [approvalId, setApprovalId] = useState("");
  const [busy, setBusy] = useState(false);

  const approvals = useAsync(() => api.approvalsOfCase(caseId), [caseId]);

  // 방향과 승인 종류는 짝이다 (API-SPEC §5-1 규칙 2). 안 맞는 승인을 고를 수 있게 두면
  // 화면이 만들어 낸 409 를 사람이 해석해야 한다.
  const wantKind = direction === "OUT_VENDOR" ? "SHIP_TO_VENDOR" : "SHIP_TO_OWNER";
  const usable = (approvals.data?.items ?? []).filter(
    (a) => a.kind === wantKind && a.decision === "APPROVED",
  );

  async function approveHere() {
    setBusy(true);
    onError(null);
    try {
      // 만들기 + 결정하기는 한 동작이다. 같은 조합으로 다시 만들면 있던 것을 돌려주므로
      // 두 번 눌러도 승인이 두 개가 되지 않는다 (API-SPEC §4-1 규칙 1)
      const created = await api.createApprovals([caseId], wantKind);
      await api.decideApprovals(
        created.map((a) => a.id),
        "APPROVED",
        "케이스 상세에서 승인",
      );
      const reloaded = await api.approvalsOfCase(caseId);
      const fresh = reloaded.items.find((a) => a.kind === wantKind && a.decision === "APPROVED");
      if (fresh) setApprovalId(String(fresh.id));
      approvals.reload();
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    setBusy(true);
    try {
      await api.createShipment({
        case_id: caseId,
        direction,
        dest_company_id: Number(dest),
        shipped_at: shippedAt,
        carrier: carrier || undefined,
        tracking,
        approval_id: Number(approvalId),
      });
      setTracking("");
      setApprovalId("");
      approvals.reload();
      onDone();
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>
        출고·송장<span className="sub">승인 없이는 등록되지 않습니다</span>
      </h2>

      <ErrorNotice error={approvals.error} />

      <div className="row" style={{ marginBottom: 12 }}>
        <label className="field">
          방향
          <select
            value={direction}
            onChange={(e) => {
              setDirection(e.target.value as "OUT_VENDOR" | "OUT_OWNER");
              setApprovalId(""); // 방향이 바뀌면 그 승인은 못 쓴다
            }}
          >
            <option value="OUT_VENDOR">SECUI 이관</option>
            <option value="OUT_OWNER">협력사 반송</option>
          </select>
        </label>
        <label className="field">
          행선지
          <select value={dest} onChange={(e) => setDest(e.target.value)}>
            <option value="">선택</option>
            {companies.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          출고일
          <input type="date" value={shippedAt} onChange={(e) => setShippedAt(e.target.value)} />
          {/*
            막지 않는다 — 실제로 그런 날짜가 필요할 수 있다(늦게 기록하는 경우).
            다만 입고보다 앞선 출고일은 장비 이력을 "나가고 나서 들어왔다" 로 만든다.
          */}
          {shippedAt !== "" && shippedAt < receivedAt && (
            <span className="muted" style={{ color: "var(--danger)" }}>
              입고일({receivedAt})보다 앞섭니다
            </span>
          )}
          {shippedAt > todayKst() && (
            <span className="muted" style={{ color: "var(--danger)" }}>
              오늘({todayKst()})보다 미래입니다
            </span>
          )}
        </label>
        <label className="field">
          택배사
          <input value={carrier} onChange={(e) => setCarrier(e.target.value)} />
        </label>
        <label className="field">
          송장번호
          <input
            value={tracking}
            placeholder="449-3097-9074"
            onChange={(e) => setTracking(e.target.value)}
          />
        </label>
        <label className="field">
          승인
          <select
            value={approvalId}
            disabled={usable.length === 0}
            onChange={(e) => setApprovalId(e.target.value)}
          >
            <option value="">
              {usable.length === 0 ? "이 방향으로 승인된 건이 없습니다" : "선택"}
            </option>
            {usable.map((a) => (
              <option key={a.id} value={String(a.id)}>
                #{a.id} · {formatKstDay(a.decided_at)}
                {a.decision_note ? ` · ${a.decision_note}` : ""}
              </option>
            ))}
          </select>
        </label>
      </div>

      {usable.length === 0 && (
        <Notice kind="info">
          {canApprove ? (
            <>
              이 방향(<strong>{direction === "OUT_VENDOR" ? "SECUI 이관" : "협력사 반송"}</strong>)
              으로 승인된 건이 없습니다. 부장님 확인을 받으셨다면 여기서 바로 승인하실 수
              있습니다.{" "}
              <button disabled={busy} onClick={approveHere}>
                이 건 승인
              </button>
            </>
          ) : (
            <>
              이 방향으로 승인된 건이 없습니다. 승인 권한이 있는 분이 먼저 승인해야
              출고를 등록할 수 있습니다.
            </>
          )}
        </Notice>
      )}

      <div className="row">
        <button
          className="primary"
          disabled={busy || dest === "" || tracking === "" || approvalId === ""}
          onClick={submit}
        >
          {busy ? "등록 중…" : "출고 등록"}
        </button>
        <span className="muted">
          승인된 건만 나갈 수 있습니다. 화면이든 API 든 우회 경로는 없습니다.
        </span>
      </div>
    </div>
  );
}

/**
 * ★ 교체 시리얼. 이 프로젝트의 제1 목적이라 **케이스 화면 맨 위쪽**에 둔다 —
 * 다른 화면으로 건너가야 보이면 그 연결은 실무에서 끊긴 것과 같다.
 *
 * 수집기가 생기기 전까지는 대표가 SECUI 화면을 보고 직접 적는 것이
 * 이 연결을 만드는 유일한 경로다 (harness/SWAP-POLICY.md §2-1).
 */
function SwapCard({
  row,
  canEdit,
  onDone,
  onError,
}: {
  row: Case;
  canEdit: boolean;
  onDone: (message: string) => void;
  onError: (error: unknown) => void;
}) {
  const [open, setOpen] = useState(false);
  const [serial, setSerial] = useState("");
  const [swappedAt, setSwappedAt] = useState("");
  const [evidence, setEvidence] = useState("");
  const [busy, setBusy] = useState(false);

  const preview = normalizeSerial(serial).norm;
  // 근거 문장을 보여주려면 교체 행 자체가 필요하다. 케이스에 실린 것은 요약값이다
  const swaps = useAsync(
    () =>
      row.replacement_serial
        ? api.swaps({ serial: row.serial_norm })
        : Promise.resolve({ items: [] }),
    [row.replacement_serial, row.serial_norm],
  );

  async function submit() {
    setBusy(true);
    onError(null);
    try {
      await api.createSwap({
        case_id: row.id,
        replacement_serial: serial,
        swapped_at: swappedAt || null,
        evidence_ref: evidence,
      });
      setOpen(false);
      setSerial("");
      setSwappedAt("");
      setEvidence("");
      onDone("교체 시리얼을 등록했습니다.");
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: number) {
    const reason = window.prompt(
      "이 교체 연결을 지웁니다. 원 시리얼과 교체 시리얼의 연결이 끊깁니다.\n지우는 이유를 적어 주세요 (감사 기록에 남습니다).",
      "",
    );
    if (reason === null) return;
    if (reason.trim().length < 2) {
      onError(new Error("지우는 이유를 2자 이상 적어 주세요."));
      return;
    }
    setBusy(true);
    onError(null);
    try {
      await api.deleteSwap(id, reason.trim());
      onDone("교체 연결을 지웠습니다.");
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  }

  const current = (swaps.data?.items ?? []).find((swap) => swap.case_id === row.id);

  return (
    <div className="card">
      <h2>
        교체 시리얼<span className="sub">SECUI 가 대신 보내준 장비</span>
      </h2>

      {row.replacement_serial === null ? (
        <>
          <p className="muted">
            아직 없습니다. SECUI 에서 교체품 시리얼을 확인하셨으면 여기에 적어 주세요 —
            <b> 이 연결이 이 시스템의 이유</b>입니다.
          </p>

          {canEdit && !open && (
            <button className="primary" onClick={() => setOpen(true)}>
              교체 시리얼 등록
            </button>
          )}

          {canEdit && open && (
            <>
              <div className="row" style={{ marginBottom: 10 }}>
                <label className="field">
                  교체 시리얼
                  <input
                    value={serial}
                    placeholder="대소문자·공백 그대로 넣어도 됩니다"
                    onChange={(e) => setSerial(e.target.value)}
                  />
                </label>
                <label className="field">
                  교체일 (모르면 비워 두세요)
                  <input
                    type="date"
                    value={swappedAt}
                    onChange={(e) => setSwappedAt(e.target.value)}
                  />
                </label>
              </div>
              <label className="field" style={{ marginBottom: 10 }}>
                근거 — 어디서 확인하셨습니까
                <input
                  value={evidence}
                  placeholder="예: SECUI 케이스 C2026090106 화면에서 확인"
                  onChange={(e) => setEvidence(e.target.value)}
                />
              </label>
              <p className="muted" style={{ marginTop: 0 }}>
                {preview !== "" && (
                  <>
                    저장될 값: <span className="mono">{preview}</span> ·{" "}
                  </>
                )}
                원 시리얼 <span className="mono">{row.serial_norm}</span> 에 잇습니다.
                SECUI 접수번호는 근거 문장에 적어 주세요 — 수집기가 원문을 실제로 가져오기
                전까지는 번호만으로 원문에 닿을 수 없습니다.
              </p>
              <div className="row">
                <button
                  className="primary"
                  disabled={busy || preview === "" || evidence.trim().length < 2}
                  onClick={submit}
                >
                  등록
                </button>
                <button disabled={busy} onClick={() => setOpen(false)}>
                  취소
                </button>
                {busy && <Busy>등록 중…</Busy>}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div className="row" style={{ gap: 24 }}>
            <div>
              <div className="muted">원 시리얼</div>
              <div className="mono">{row.serial_norm}</div>
            </div>
            <div>
              <div className="muted">→ 교체 시리얼</div>
              <div className="mono">
                <a
                  href={`#/devices/${encodeURIComponent(row.replacement_serial)}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`#/devices/${encodeURIComponent(row.replacement_serial!)}`);
                  }}
                >
                  {row.replacement_serial}
                </a>
              </div>
            </div>
            <div>
              <div className="muted">교체일</div>
              <div>
                <Cell value={row.swapped_at} />
              </div>
            </div>
            <div>
              <div className="muted">SECUI 접수번호</div>
              <div>
                <Cell value={row.secui_case_no} />
              </div>
            </div>
          </div>

          {current && (
            <p className="muted" style={{ marginTop: 10, marginBottom: 0 }}>
              근거: {current.evidence_ref}
              {current.source === "MANUAL" ? " (직접 입력)" : " (SECUI 수집)"}
              {canEdit && (
                <>
                  {" · "}
                  <button className="ghost" disabled={busy} onClick={() => remove(current.id)}>
                    잘못 적었습니다 — 지우기
                  </button>
                </>
              )}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** 콘솔 로그는 종류를 고를 수 있어야 한다 (harness/EVIDENCE-POLICY.md §2) */
const EVIDENCE_KIND_LABELS: Array<[string, string]> = [
  ["error_log", "에러 콘솔 로그"],
  ["boot_log", "부팅 콘솔 로그"],
  ["init_log", "초기화 전/후 로그"],
  ["gui_shot", "화면 캡처"],
  ["config", "구성 파일"],
];

function bytesLabel(n: number | null): string {
  if (n === null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * 증적 (harness/EVIDENCE-POLICY.md).
 *
 * 대표는 콘솔 로그를 **터미널에서 복사해** 온다 — 파일로 저장했다가 고르는 게 아니라.
 * 그래서 붙여넣기가 기본이고 파일 선택이 보조다. 붙여넣은 글자가 그대로 원문 파일이 된다.
 *
 * **증적이 없다고 아무것도 막지 않는다**(SOUL S1) — 이 카드는 늘 보이고, 비어 있어도
 * 케이스는 그대로 진행된다.
 */
function EvidenceCard({
  caseId,
  canUpload,
  onError,
}: {
  caseId: number;
  canUpload: boolean;
  onError: (error: unknown) => void;
}) {
  const list = useAsync(() => api.evidence({ case_id: caseId }), [caseId]);
  const [kind, setKind] = useState("error_log");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function send(file: File) {
    setBusy(true);
    onError(null);
    setMessage(null);
    try {
      const saved = await api.uploadEvidence(
        { case_id: caseId, kind: kind as EvidenceUploadMeta["kind"] },
        file,
      );
      setMessage(`${saved.filename} 을(를) 올렸습니다 (${bytesLabel(saved.bytes)}).`);
      setText("");
      list.reload();
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  }

  async function sendPasted() {
    if (text.trim() === "") return;
    const stamp = new Date().toISOString().replace(/[-:]/g, "").slice(0, 15);
    // 붙여넣은 글자를 그대로 파일로 만든다 — 다듬거나 줄이지 않는다 (SOUL S3)
    await send(new File([text], `${kind}_${stamp}.log`, { type: "text/plain" }));
  }

  const items = list.data?.items ?? [];

  return (
    <div className="card">
      <h2>
        증적<span className="sub">원문 그대로 보관합니다 — 없어도 진행에 지장은 없습니다</span>
      </h2>

      {message && <Notice kind="ok">{message}</Notice>}
      <ErrorNotice error={list.error} />

      {canUpload && (
        <>
          <div className="row" style={{ marginBottom: 8 }}>
            <label className="field">
              종류
              <select value={kind} onChange={(e) => setKind(e.target.value)}>
                {EVIDENCE_KIND_LABELS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              파일로 올리기 (캡처 등)
              <input
                type="file"
                disabled={busy}
                onChange={(e) => {
                  const chosen = e.target.files?.[0];
                  if (chosen) void send(chosen);
                  e.target.value = "";
                }}
              />
            </label>
            {busy && <Busy>올리는 중…</Busy>}
          </div>

          <label className="field" style={{ marginBottom: 8 }}>
            콘솔 로그 붙여넣기
            <textarea
              rows={5}
              value={text}
              disabled={busy}
              placeholder="터미널에서 복사한 로그를 그대로 붙여넣으세요. 줄이거나 다듬지 않고 원문 그대로 보관합니다."
              onChange={(e) => setText(e.target.value)}
            />
          </label>
          <div className="row" style={{ marginBottom: 14 }}>
            <button className="primary" disabled={busy || text.trim() === ""} onClick={sendPasted}>
              붙여넣은 로그 올리기
            </button>
            <span className="muted">
              {text.trim() === "" ? "" : `${text.split("\n").length}줄 · ${bytesLabel(new Blob([text]).size)}`}
            </span>
          </div>
        </>
      )}

      {list.loading ? (
        <Busy />
      ) : items.length === 0 ? (
        <p className="muted" style={{ marginBottom: 0 }}>
          아직 없습니다. 부팅조차 안 되는 장비도 있습니다 — 없어도 됩니다.
        </p>
      ) : (
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th>종류</th>
                <th>파일</th>
                <th>크기</th>
                <th>sha256</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id}>
                  <td>
                    {EVIDENCE_KIND_LABELS.find(([v]) => v === e.kind)?.[1] ?? e.kind}
                  </td>
                  <td>
                    <a href={api.evidenceUrl(e.id)} target="_blank" rel="noreferrer">
                      {e.filename ?? `증적 ${e.id}`}
                    </a>
                  </td>
                  <td className="num">{bytesLabel(e.bytes)}</td>
                  {/* 무결성을 확인할 수 있어야 증적이다 (EVIDENCE-POLICY §4) */}
                  <td className="mono muted">{e.sha256?.slice(0, 12) ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
